import { defModule, Task } from '@st/redux'
import { Effect } from 'effect'
import { observeSnackbarEvents, type SnackbarVariant } from '~/effects/snackbar'

export type SnackbarState =
  | { status: 'idle'; showSequence: number }
  | {
      status: 'showing'
      message: string
      variant: SnackbarVariant
      timeoutMs: number
      showSequence: number
    }

export type SnackbarMessage =
  | { type: 'show'; message: string; timeoutMs?: number; variant?: SnackbarVariant }
  | { type: 'dismiss' }

type SnackbarInit = undefined

const DEFAULT_TIMEOUT_MS = 5000
const DEFAULT_VARIANT: SnackbarVariant = 'default'

const observeSnackbar = Task.define(() =>
  Task.pipe(
    observeSnackbarEvents,
    Task.map((event): SnackbarMessage => event)
  )
)

const dismissAfter = Task.define((_showSequence: number, timeoutMs: number) =>
  Effect.sleep(`${timeoutMs} millis`).pipe(Effect.as({ type: 'dismiss' } as const))
)

export const snackbarModule = defModule<{
  state: SnackbarState
  message: SnackbarMessage
  init: SnackbarInit
}>({
  init: () => ({
    status: 'idle',
    showSequence: 0
  }),
  tasks: (state) => [
    observeSnackbar(),
    ...(state.status == 'showing' ? [dismissAfter(state.showSequence, state.timeoutMs)] : [])
  ],
  update: (state, message): SnackbarState => {
    switch (message.type) {
      case 'show':
        return {
          status: 'showing',
          message: message.message,
          variant: message.variant ?? DEFAULT_VARIANT,
          timeoutMs: message.timeoutMs ?? DEFAULT_TIMEOUT_MS,
          showSequence: state.showSequence + 1
        }
      case 'dismiss':
        return {
          status: 'idle',
          showSequence: state.showSequence
        }
    }
  }
})
